import React, { useState } from 'react';
import { Activity, Maximize2, Minimize2, ChevronUp, ChevronDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const HeadToHeadChart = ({ 
  chartData, 
  manager1Name, 
  manager2Name, 
  gameCount,
  className = "" 
}) => {
  // State for managing expanded/collapsed facets
  const [expandedFacets, setExpandedFacets] = useState({});
  const [maximizedFacet, setMaximizedFacet] = useState(null);
  
  // Group data by game type for faceted display
  const groupDataByGameType = (data) => {
    const grouped = data.reduce((acc, game) => {
      const gameType = game.type;
      if (!acc[gameType]) {
        acc[gameType] = [];
      }
      acc[gameType].push(game);
      return acc;
    }, {});

    // Sort game types in the order: Consolation, Playoffs, Regular Season
    const gameTypeOrder = ['Consolation', 'Playoffs', 'Regular Season'];
    const sortedGrouped = {};
    gameTypeOrder.forEach(type => {
      if (grouped[type] && grouped[type].length > 0) {
        // Sort by extracting year and week from the week string for proper chronological order
        sortedGrouped[type] = grouped[type].sort((a, b) => {
          // Extract year and week from week string (e.g. "2021 - Week 1")
          const parseWeek = (weekStr) => {
            const match = weekStr?.match(/(\d{4})\s*-\s*Week\s*(\d+)/);
            if (match) {
              return { year: parseInt(match[1]), week: parseInt(match[2]) };
            }
            return { year: 0, week: 0 };
          };
          
          const aWeek = parseWeek(a.week);
          const bWeek = parseWeek(b.week);
          
          // First sort by year, then by week
          if (aWeek.year !== bWeek.year) {
            return aWeek.year - bWeek.year;
          }
          return aWeek.week - bWeek.week;
        });
      }
    });

    return sortedGrouped;
  };

  // Transform data for grouped bar chart
  const transformDataForBars = (games) => {
    // Sort by year and week for correct chronological order
    const sortedGames = [...games].sort((a, b) => {
      // Extract year and week from week string (e.g. "2021 - Week 1")
      const parseWeek = (weekStr) => {
        const match = weekStr?.match(/(\d{4})\s*-\s*Week\s*(\d+)/);
        if (match) {
          return { year: parseInt(match[1]), week: parseInt(match[2]) };
        }
        return { year: 0, week: 0 };
      };
      
      const aWeek = parseWeek(a.week);
      const bWeek = parseWeek(b.week);
      
      // First sort by year, then by week
      if (aWeek.year !== bWeek.year) {
        return aWeek.year - bWeek.year;
      }
      return aWeek.week - bWeek.week;
    });
    
    return sortedGames.map((game, index) => {
      const manager1Score = game[manager1Name];
      const manager2Score = game[manager2Name];
      const winner = manager1Score > manager2Score ? manager1Name : manager2Name;
      const margin = Math.abs(manager1Score - manager2Score);
      
      return {
        game: `Game ${index + 1}`,
        gameLabel: game.week?.replace(/(\d{4})\s*-\s*Week\s*(\d+)/, (match, year, week) => `W${week} '${year.slice(-2)}`) || `Game ${index + 1}`,
        [manager1Name]: manager1Score,
        [manager2Name]: manager2Score,
        winner: winner,
        margin: margin,
        isCloseGame: margin < 10, // Close game if margin < 10 points
        originalData: game,
        chronologicalOrder: index // Track the chronological position
      };
    });
  };

  // Gradient definitions for winner effects
  const WinnerGradients = () => (
    <defs>
      <linearGradient id="winner-gradient-purple" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#a855f7" stopOpacity="1" />
        <stop offset="100%" stopColor="#8b5cf6" stopOpacity="1" />
      </linearGradient>
      <linearGradient id="winner-gradient-blue" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0ea5e9" stopOpacity="1" />
        <stop offset="100%" stopColor="#0284c7" stopOpacity="1" />
      </linearGradient>
    </defs>
  );

  // Custom bar component that adds winner indicators
  const CustomBar = ({ fill, ...props }) => {
    const { payload, width, height, x, y } = props;
    if (!payload) return <Bar fill={fill} {...props} />;
    
    const isWinner = payload.winner === (fill === '#8b5cf6' ? manager1Name : manager2Name);
    const isCloseGame = payload.isCloseGame;
    
    // Enhanced fill with winner glow effect
    const enhancedFill = isWinner 
      ? `url(#winner-gradient-${fill === '#8b5cf6' ? 'purple' : 'blue'})` 
      : fill;
    
    return (
      <g>
        {/* Main bar */}
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={enhancedFill}
          rx={fill === '#8b5cf6' ? 2 : 2}
          className={`transition-all duration-200 ${isWinner ? 'drop-shadow-sm' : ''}`}
          style={{
            filter: isWinner ? 'brightness(1.15)' : 'brightness(0.75)',
            stroke: isWinner ? (fill === '#8b5cf6' ? '#7c3aed' : '#0284c7') : 'transparent',
            strokeWidth: isWinner ? 1 : 0,
            opacity: isWinner ? 1 : 0.8,
          }}
        />
        
        {/* Winner crown indicator */}
        {isWinner && height > 20 && (
          <g>
            <circle
              cx={x + width / 2}
              cy={y - 8}
              r={6}
              fill={fill === '#8b5cf6' ? '#fbbf24' : '#fbbf24'}
              className="drop-shadow-sm"
            />
            <text
              x={x + width / 2}
              y={y - 5}
              textAnchor="middle"
              fontSize="8"
              fill="#92400e"
              fontWeight="bold"
            >
              ♛
            </text>
          </g>
        )}
        
        {/* Close game indicator */}
        {isCloseGame && (
          <rect
            x={x}
            y={y + height - 3}
            width={width}
            height={3}
            fill="#f59e0b"
            rx={1}
            className="animate-pulse"
          />
        )}
      </g>
    );
  };

  // Custom tooltip component for better formatting
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-gray-200">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          <p className="text-sm text-gray-600 mb-1">Game Type: {data.originalData.type}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  {entry.dataKey}:
                </span>
                <span className="font-semibold ml-2">{entry.value?.toFixed(1)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex justify-between text-sm">
              <span>Winner:</span>
              <span className={`font-semibold flex items-center ${
                data[manager1Name] > data[manager2Name] ? 'text-purple-600' : 'text-blue-600'
              }`}>
                <span className="mr-1">♛</span>
                {data[manager1Name] > data[manager2Name] ? manager1Name : manager2Name}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Margin:</span>
              <span className={`font-semibold ${
                data.isCloseGame ? 'text-amber-600 font-bold' : 'text-gray-700'
              }`}>
                {Math.abs(data[manager1Name] - data[manager2Name]).toFixed(1)} pts
                {data.isCloseGame && <span className="ml-1 text-xs">🔥</span>}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const groupedData = groupDataByGameType(chartData);
  const gameTypeCount = Object.keys(groupedData).length;
  const isMultipleCharts = gameTypeCount > 1;

  // Initialize expanded state for all facets
  React.useEffect(() => {
    if (gameTypeCount > 0) {
      const initialState = {};
      Object.keys(groupedData).forEach(gameType => {
        initialState[gameType] = true; // All expanded by default
      });
      setExpandedFacets(initialState);
    }
  }, [gameTypeCount]);

  // Toggle functions
  const toggleFacet = (gameType) => {
    setExpandedFacets(prev => ({
      ...prev,
      [gameType]: !prev[gameType]
    }));
  };

  const maximizeFacet = (gameType) => {
    setMaximizedFacet(gameType === maximizedFacet ? null : gameType);
  };

  const expandAllFacets = () => {
    const allExpanded = {};
    Object.keys(groupedData).forEach(gameType => {
      allExpanded[gameType] = true;
    });
    setExpandedFacets(allExpanded);
    setMaximizedFacet(null);
  };

  const collapseAllFacets = () => {
    const allCollapsed = {};
    Object.keys(groupedData).forEach(gameType => {
      allCollapsed[gameType] = false;
    });
    setExpandedFacets(allCollapsed);
    setMaximizedFacet(null);
  };

  // Calculate dynamic height based on number of games and screen size
  const getChartHeight = (gameCount, isMaximized) => {
    if (isMaximized) return 500;
    
    // Base height with better spacing
    const baseHeight = 280;
    
    // Adjust height based on number of games (more games = slightly taller for readability)
    const gameAdjustment = Math.min(gameCount * 8, 60);
    
    return baseHeight + gameAdjustment;
  };

  // Calculate grid layout
  const getGridClass = () => {
    const expandedCount = Object.values(expandedFacets).filter(Boolean).length;
    if (expandedCount <= 1) return 'grid-cols-1';
    if (expandedCount === 2) return 'grid-cols-1 lg:grid-cols-2';
    return 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3';
  };

  if (!chartData || chartData.length === 0) {
    return (
      <div className={`bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Head-to-Head Data</h3>
            <p className="text-gray-500">Select two different managers to see their matchup history.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 ${className}`}>
      {/* Header */}
      <div className="p-6 lg:p-8 border-b border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center mb-2">
              <Activity className="h-6 w-6 lg:h-8 lg:w-8 mr-3 lg:mr-4 text-purple-500" />
              Head-to-Head Matchups
            </h3>
            <p className="text-gray-600">
              <span className="font-semibold text-purple-600">{manager1Name}</span> vs{' '}
              <span className="font-semibold text-blue-600">{manager2Name}</span>
            </p>
          </div>
          
          {/* Controls */}
          {isMultipleCharts && (
            <div className="flex items-center space-x-2">
              <button
                onClick={expandAllFacets}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200 border border-blue-200"
              >
                <ChevronDown className="h-4 w-4 inline mr-1" />
                Expand All
              </button>
              <button
                onClick={collapseAllFacets}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200"
              >
                <ChevronUp className="h-4 w-4 inline mr-1" />
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="p-4 lg:p-6">
        {/* Maximized Chart */}
        {maximizedFacet && (
          <div className="mb-6">
            {Object.entries(groupedData).map(([gameType, games]) => {
              if (gameType !== maximizedFacet) return null;
              
              const barData = transformDataForBars(games);
              
              return (
                <div key={gameType} className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                    <h4 className="text-xl font-semibold text-gray-800">{gameType}</h4>
                    <button
                      onClick={() => maximizeFacet(gameType)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                      title="Restore"
                    >
                      <Minimize2 className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={getChartHeight(games.length, true)}>
                      <BarChart
                        data={barData}
                        margin={{ top: 30, right: 20, left: 20, bottom: 40 }}
                      >
                        <WinnerGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis 
                          dataKey="gameLabel"
                          stroke="#6b7280"
                          fontSize={12}
                          angle={-45}
                          textAnchor="end"
                          height={40}
                          interval={0}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          stroke="#6b7280" 
                          fontSize={12}
                          label={{ value: 'Points', angle: -90, position: 'insideLeft', style: { fontSize: '12px' } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey={manager1Name} 
                          shape={<CustomBar fill="#8b5cf6" />}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={40}
                        />
                        <Bar 
                          dataKey={manager2Name} 
                          shape={<CustomBar fill="#0ea5e9" />}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={40}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    
                    <div className="mt-3 text-center border-t border-gray-100 pt-3">
                      <div className="text-sm text-blue-600 font-medium">
                        {games.length} game{games.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Regular Grid Layout */}
        <div className={maximizedFacet ? 'hidden' : `grid ${getGridClass()} gap-4 lg:gap-6`}>
          {Object.entries(groupedData).map(([gameType, games]) => {
            const barData = transformDataForBars(games);
            const isExpanded = expandedFacets[gameType];
            
            if (!isExpanded) return null;
            
            return (
              <div key={gameType} className="transition-all duration-300">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full">
                  {/* Chart Header with Controls */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                    <h4 className="text-lg font-semibold text-gray-800">{gameType}</h4>
                    
                    {isMultipleCharts && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => maximizeFacet(gameType)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          title="Maximize"
                        >
                          <Maximize2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleFacet(gameType)}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
                          title="Collapse"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Optimized Chart Content */}
                  <div className="p-3">
                    <ResponsiveContainer width="100%" height={getChartHeight(games.length, false)}>
                      <BarChart
                        data={barData}
                        margin={{ top: 25, right: 15, left: 15, bottom: 35 }}
                      >
                        <WinnerGradients />
                        <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                        <XAxis 
                          dataKey="gameLabel"
                          stroke="#6b7280"
                          fontSize={10}
                          angle={-45}
                          textAnchor="end"
                          height={35}
                          interval={0}
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis 
                          stroke="#6b7280" 
                          fontSize={10}
                          width={40}
                          label={{ value: 'Pts', angle: -90, position: 'insideLeft', style: { fontSize: '10px' } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey={manager1Name} 
                          shape={<CustomBar fill="#8b5cf6" />}
                          radius={[2, 2, 0, 0]}
                          maxBarSize={30}
                        />
                        <Bar 
                          dataKey={manager2Name} 
                          shape={<CustomBar fill="#0ea5e9" />}
                          radius={[2, 2, 0, 0]}
                          maxBarSize={30}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    
                    {/* Compact Game Summary */}
                    <div className="mt-2 text-center border-t border-gray-100 pt-2">
                      <div className="text-xs text-blue-600 font-medium">
                        {games.length} game{games.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Collapsed Charts - Compact Row */}
        {Object.entries(groupedData).some(([gameType]) => !expandedFacets[gameType] && !maximizedFacet) && (
          <div className="flex flex-wrap gap-3 mt-4">
            {Object.entries(groupedData).map(([gameType, games]) => {
              const isExpanded = expandedFacets[gameType];
              
              if (isExpanded || maximizedFacet) return null;
              
              return (
                <div 
                  key={gameType}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => toggleFacet(gameType)}
                >
                  <div className="flex items-center justify-between p-3 bg-gray-50">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">{gameType}</h4>
                      <div className="text-xs text-gray-600">
                        {games.length} game{games.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeadToHeadChart;